import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { take } from 'rxjs/operators';
import { ServiceLocator } from '../ServiceLocator';
import { PlusAlertsService } from './plus-alerts.service';
import { UtilService } from './util.service';

@Injectable()
export class BreadCrumbService {
  private utilService;
  private navigationParams = null;
  public breadCrumbNodes = [];
  public breadCrumb = new BehaviorSubject<any>({}); // true is the initial value
  public readonly INITIAL_ROUTE: String = "/home/dashboard";
  breadCrumb$ = this.breadCrumb.asObservable();

  constructor(private router: Router, private currentRoute: ActivatedRoute, private plusAlertService: PlusAlertsService) {
    this.utilService = ServiceLocator.injector.get(UtilService)
    let _breadCrumbNodes = sessionStorage.getItem("breadCrumbNodes");
    let _navigationParams = sessionStorage.getItem("navigationParams");

    if (_breadCrumbNodes)
      this.breadCrumbNodes = JSON.parse(_breadCrumbNodes);

    if (_navigationParams)
      this.navigationParams = JSON.parse(_navigationParams);

    //this.utilService

    this.checkInitNodes();
  }

  /**
   * Mappea la nueva opcion del breadcrumb
   * @param value Obj con title, routerLink, isNewRoute
   */
  add(value: any) {
    if (value) {
      if (!this.breadCrumbNodes.find(x => x.title === value.title)) {
        this.breadCrumbNodes.push(value);
        this.breadCrumb.next(value);
        sessionStorage.setItem("breadCrumbNodes", JSON.stringify(this.breadCrumbNodes));
        sessionStorage.setItem("navigationParams", JSON.stringify(this.navigationParams));
      }
    }
  }

  /**
   * Mappea la nueva opcion del breadcrumb y persiste la vista actual
   * @param _title titulo de la nueva vista
   * @param _link URL de la nueva vista
   * @param _isNewRoute true si encola la vista en el breadcrumb, false para sobreescribir
   * @param refView CtView de la vista actual
   */
  addBreadCrumb(_title: string, _link: string, _isNewRoute: boolean, refView?: any) {
    this.utilService.checkStack(refView);
    this.add({ title: _title, routerLink: _link, isNewRoute: _isNewRoute });
  }

  /**
   * Agrega la vista actual a la pila de vistas, navega hacia la nueva y actualiza el breadcrumb
   * @param basePath porcion de URL que especifica si es vista de Forms, V1 o V2
   * @param newViewCode codigo de la nueva vista
   * @param newViewTitle titulo de la nueva vista (opcion de menu)
   * @param _queryParams parametros en base64 a enviar por URL
   * @param isNewRoute false si encola la vista en el breadcrumb, true para sobreescribir
   * @param refView CtView de la vista actual
   */
  navigateTo(basePath: string, newViewCode: string, newViewTitle: string, _queryParams?: any, isNewRoute?: boolean, refView?: any) {
    if (this.utilService.getViewStack().indexOf(newViewCode) !== -1)
      return false;

    let link = [basePath + newViewCode];
    this.router.navigate(link);
    this.plusAlertService.getContinueFlag().pipe(
      take(1))
      .subscribe((confirm) => {
        if (confirm || !this.plusAlertService.getUsavedData()) {
          this.navigationParams = _queryParams;
          // this.addBreadCrumb(newViewTitle, link[0], isNewRoute, refView);
        }
      })
    return true;
  }

  /**
   * Lista de parametros de URL
   */
  getUrlParams() {
    let args = null;

    if (this.navigationParams)
      args = Object.keys(this.navigationParams);

    return args;
  }

  /**
   * Retorna el valor de un parametro de URL
   * @param paramName Nombre del parametro a obtener
   */
  getUrlParamValue(paramName: string) {
    let argVal = null;

    if (this.navigationParams)
      argVal = this.navigationParams[paramName];

    return argVal;
  }


  /**
   * true si la vista fue accedida por navegacion de otra vista, o false si fue accedida normalmente por menu
   * @param viewId Id de la vista
   */
  isViewReferenced(viewId: string) {
    this.checkInitNodes();

    let hasRef = false;
    let rootNode = this.breadCrumbNodes.find(x => x.routerLink.indexOf(viewId) !== -1);

    if (rootNode && this.breadCrumbNodes.indexOf(rootNode) > 1)
      hasRef = true;

    return hasRef;
  }

  /**
   * Revisa si la vista esta mapeada en memoria, si no, la busca en local storage
   * @param viewId codigo de vista
   */
  isViewMapped(viewId: string) {
    let ref = this.utilService.getViewStack().find(x => x.id === viewId);
    let exists = (ref != null && ref != undefined);

    return exists;
  }

  /**
   * Remueve la ultima vista de la pila y navega a la penultima
   * @param viewRoute remueve los nodos sobrantes despues de encontrar la vista
   */
  pop(viewRoute?: any) {
    let success = false;
    let stack = this.utilService.getViewStack();

    if (stack.length > 0) {
      // reconstruiye los breadcrums desde la primera vista hasta la que se selecciono
      let viewIndx = 0;

      while (!success && viewIndx < this.breadCrumbNodes.length) {
        let bcNode = this.breadCrumbNodes[viewIndx++];
        this.breadCrumb.next(bcNode);

        if (bcNode.routerLink === viewRoute) {
          stack.splice((viewIndx - 1));
          this.breadCrumbNodes.splice(viewIndx);
          this.router.navigate([bcNode.routerLink]);
          success = true;
        }
      }
      this.setViewStack(stack);

      sessionStorage.setItem("breadCrumbNodes", JSON.stringify(this.breadCrumbNodes));
    }

    return success;
  }

  /**
   * Limpia los breadcrumbs y la pila de vistas
   */
  clear(preserveRoute: boolean = false) {
    this.setViewStack([]);
    this.breadCrumbNodes = [];
    sessionStorage.setItem("breadCrumbNodes", JSON.stringify(this.breadCrumbNodes));
    localStorage.setItem('currentView', null);

    if (!preserveRoute) {
      this.router.navigate([this.INITIAL_ROUTE]);
      this.plusAlertService.getContinueFlag().pipe(
        take(1))
        .subscribe((confirm) => {
          if (confirm || !this.plusAlertService.getUsavedData()) {
            this.checkInitNodes();
          }
        });
    }
  }

  private checkInitNodes() {
    if (this.breadCrumbNodes.length === 0)
      this.add({ title: 'Pagina principal', routerLink: this.INITIAL_ROUTE });

    else if (this.breadCrumbNodes[0].routerLink !== this.INITIAL_ROUTE) {
      this.breadCrumbNodes.splice(0, 0, { title: 'Pagina principal', routerLink: this.INITIAL_ROUTE });
      sessionStorage.setItem("breadCrumbNodes", JSON.stringify(this.breadCrumbNodes));
    }
  }

  /**
  * Obtiene el nodo cachado con la especificacion y navegacion de la vista
  * @param viewId Id de la vista
  */
  getCachedView(viewId: string) {
    return this.utilService.getCachedView(viewId);
  }

  private setViewStack(stack) {
    if (stack.length == 0 && this.plusAlertService.getUsavedView()) {
      this.plusAlertService.getContinueFlag().pipe(
        take(1))
        .subscribe((confirm) => {
          if (confirm && this.plusAlertService.getUsavedView()) {
            sessionStorage.setItem("savedStack", JSON.stringify(this.utilService.getViewStack()));
            setTimeout(() => {
              this.utilService.setViewStack(stack);;
            });
            this.plusAlertService.setUsavedView(false);
          } else {
            this.utilService.setViewStack(stack);
            sessionStorage.setItem("savedStack", JSON.stringify(stack));
          }
        })
    } else {
      this.utilService.setViewStack(stack);;
    }
  }
}
